/**
 * WhatsAppConnectFlow.jsx
 *
 * Connect a WhatsApp Business number to a virtual repo in two steps:
 *   1. Authenticate the customer's WhatsApp Business account
 *        • Embedded Signup popup (partner flow), OR
 *        • Manual (dev): paste a WABA id + System-User token.
 *   2. Number picker — we list EVERY number on the WABA; numbers already linked
 *      to a repo are shown but disabled ("Linked to {repo}"). The customer picks
 *      an unlinked number, which is then bound to the selected repo.
 *
 * Both auth paths funnel through POST /whatsapp/list-numbers (returns the numbers
 * + a short-lived connectToken); the pick is committed via POST /whatsapp/connect-selected.
 */

import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Config } from 'config/envConfig';
import { alert, AlertType, Button, Dropdown, Input, LoadingView } from '@quyva/shared-ui';
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons';
import { faArrowLeft, faInfoCircle, faCircle, faLock } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { getUrl } from '../../utils';

const FIELD_BTN =
    'w-full px-3 py-2 text-sm border border-backgrounds-border rounded-lg truncate text-left';

// Meta FB SDK config — set at runtime in public/config.js (window.APP_CONFIG).
const META_APP_ID  = Config.META_APP_ID  || '';
const WA_CONFIG_ID = Config.WA_CONFIG_ID || '';

// Track our own init so a remount still guarantees FB.init ran.
let fbInitialized = false;

/** Load the Facebook JS SDK and ensure FB.init() has run exactly once. */
const loadFBSDK = () => {
    if (!META_APP_ID) return Promise.reject(new Error('Meta App ID is not configured (window.APP_CONFIG).'));
    if (window.FB && fbInitialized) return Promise.resolve();

    return new Promise((resolve) => {
        const doInit = () => {
            window.FB.init({ appId: META_APP_ID, autoLogAppEvents: true, xfbml: true, version: 'v19.0' });
            fbInitialized = true;
            resolve();
        };
        if (window.FB) { doInit(); return; }
        window.fbAsyncInit = doInit;
        if (!document.getElementById('facebook-jssdk')) {
            const script = document.createElement('script');
            script.id = 'facebook-jssdk';
            script.src = 'https://connect.facebook.net/en_US/sdk.js';
            script.async = true;
            document.body.appendChild(script);
        }
    });
};

export const WhatsAppConnectFlow = ({ user, orgId, onSuccess, onCancel, contextRepo }) => {
    const [virtualRepos, setVirtualRepos] = useState([]);
    const [selectedRepo, setSelectedRepo] = useState(null);
    const [loadingData,  setLoadingData]  = useState(true);
    const [sdkReady,     setSdkReady]      = useState(false);

    const [step,            setStep]            = useState('auth');   // 'auth' | 'picker'
    const [numbers,         setNumbers]         = useState([]);
    const [connectToken,    setConnectToken]    = useState(null);
    const [selectedPhoneId, setSelectedPhoneId] = useState(null);
    const [busy,            setBusy]            = useState(false);

    const [showManual, setShowManual] = useState(false);
    const [manual,     setManual]     = useState({ wabaId: '', accessToken: '' });

    // Embedded Signup posts the shared WABA/phone ids via a window `message` event.
    // The ES access token is scoped to that WABA and can't call /me/businesses, so
    // we must forward this waba_id to the backend (otherwise it falls back to
    // /me/businesses and fails with "#100 Missing Permission").
    const sessionInfoRef = useRef({ wabaId: null, phoneNumberId: null });

    const base = `http://${Config.NODE_SERVER_HOST}:${Config.NODE_SERVER_PORT}`;
    const authHeaders = { Authorization: encodeURIComponent(user.token) };

    useEffect(() => {
        loadFBSDK()
            .then(() => setSdkReady(true))
            .catch((err) => alert(AlertType.ERROR, 'Meta SDK not ready: ' + err.message));
        loadOrgData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Listen for the Embedded Signup session-info event from the popup.
    useEffect(() => {
        const onMessage = (event) => {
            if (event.origin !== 'https://www.facebook.com' && event.origin !== 'https://web.facebook.com') return;
            try {
                const msg = JSON.parse(event.data);
                if (msg.type === 'WA_EMBEDDED_SIGNUP' && msg.data) {
                    sessionInfoRef.current = {
                        wabaId:        msg.data.waba_id || null,
                        phoneNumberId: msg.data.phone_number_id || null,
                    };
                }
            } catch (_e) { /* non-JSON SDK messages — ignore */ }
        };
        window.addEventListener('message', onMessage);
        return () => window.removeEventListener('message', onMessage);
    }, []);

    const loadOrgData = async () => {
        try {
            setLoadingData(true);
            // The repo dropdown lists org repos; users with no org connect from
            // Configure Repository, where the repo is fixed (contextRepo).
            if (orgId) {
                const reposResp = await axios.get(getUrl(`${base}/whatsapp/virtual-repos/${orgId}`), { headers: authHeaders });
                setVirtualRepos(reposResp.data?.repos || reposResp.data || []);
            }
            if (contextRepo?.repoid) {
                setSelectedRepo({ repoid: contextRepo.repoid, repo_name: contextRepo.name });
            }
        } catch (err) {
            alert(AlertType.ERROR, 'Failed to load repos: ' + err.message);
        } finally {
            setLoadingData(false);
        }
    };

    const repoId = () => contextRepo?.repoid || selectedRepo?.repoid;

    // ── Step 1 → Step 2: authenticate + list numbers (both paths funnel here) ──
    const listNumbers = async (payload) => {
        if (!repoId()) { alert(AlertType.WARNING, 'Select a virtual repo first.'); setBusy(false); return; }
        setBusy(true);
        try {
            const resp = await axios.post(
                getUrl(`${base}/whatsapp/list-numbers`),
                { orgId, ...payload },
                { headers: authHeaders }
            );
            const list = resp.data?.numbers || [];
            setNumbers(list);
            setConnectToken(resp.data?.connectToken || null);
            const firstFree = list.find((n) => !n.linked);
            setSelectedPhoneId(firstFree ? firstFree.phoneNumberId : null);
            setStep('picker');
        } catch (err) {
            alert(
                AlertType.ERROR,
                'Could not load numbers: ' + (err.response?.data?.details || err.response?.data?.error || err.message)
            );
        } finally {
            setBusy(false);
        }
    };

    // Embedded Signup: FB.login (non-async callback) → code → listNumbers({code})
    const handleConnectAccount = () => {
        if (!sdkReady || !repoId()) return;
        setBusy(true);
        window.FB.login(
            (resp) => {
                if (!resp.authResponse?.code) {
                    setBusy(false);
                    alert(AlertType.WARNING, 'WhatsApp connection cancelled.');
                    return;
                }
                // Pass the waba_id captured from the ES session-info event so the
                // backend reads the WABA's numbers directly instead of calling
                // /me/businesses (which the scoped ES token can't do → #100).
                // ES codes are redirect-less, so we send no redirect_uri.
                listNumbers({
                    code:   resp.authResponse.code,
                    wabaId: sessionInfoRef.current?.wabaId || undefined,
                });
            },
            {
                config_id:                      WA_CONFIG_ID,
                response_type:                  'code',
                override_default_response_type: true,
                extras: { setup: {}, featureType: '', sessionInfoVersion: '3' },
            }
        );
    };

    // Manual: paste WABA + System-User token → listNumbers({accessToken, wabaId})
    const handleFetchManual = () => {
        if (!manual.wabaId || !manual.accessToken) {
            alert(AlertType.WARNING, 'WABA ID and Access Token are required.');
            return;
        }
        listNumbers({ accessToken: manual.accessToken.trim(), wabaId: manual.wabaId.trim() });
    };

    // ── Step 2 → commit: bind the chosen number ──
    const handleConnectSelected = async () => {
        if (!selectedPhoneId || !connectToken) return;
        setBusy(true);
        try {
            await axios.post(
                getUrl(`${base}/whatsapp/connect-selected`),
                { connectToken, phoneNumberId: selectedPhoneId, orgId, repoId: repoId() },
                { headers: authHeaders }
            );
            onSuccess();
        } catch (err) {
            alert(AlertType.ERROR, 'Failed to connect: ' + (err.response?.data?.error || err.message));
        } finally {
            setBusy(false);
        }
    };

    if (loadingData) return <LoadingView text="Loading…" />;

    const repoItems = virtualRepos.map((r) => ({ label: r.repo_name, action: () => setSelectedRepo(r) }));

    return (
        <div className="p-6 max-w-[560px]">
            {/* Header */}
            <div className="flex items-center gap-3 mb-6">
                <Button
                    icon={faArrowLeft}
                    hoverText="Back"
                    onClick={step === 'picker' ? () => setStep('auth') : onCancel}
                />
                <FontAwesomeIcon icon={faWhatsapp} className="text-2xl text-texts-tertiary400" />
                <h3 className="m-0 text-base font-semibold text-texts-secondary">Connect WhatsApp Number</h3>
            </div>

            {/* Virtual repo (locked when opened from Configure Repository) */}
            <div className="mb-5">
                <label className="block text-sm font-semibold text-texts-secondary mb-1.5">
                    Virtual Repo <span className="text-buttons-alert400">*</span>
                </label>
                {contextRepo ? (
                    <div className="w-full px-3 py-2 text-sm border border-backgrounds-border rounded-lg bg-backgrounds-secondary text-texts-secondary">
                        {contextRepo.name}
                    </div>
                ) : (
                    <Dropdown
                        dropDownList={repoItems}
                        showButtonText={true}
                        buttonText={selectedRepo?.repo_name || 'Select a virtual repo'}
                        listTitleText="Select a virtual repo"
                        placement="bottomLeft"
                        buttonClassName={FIELD_BTN}
                    />
                )}
            </div>

            {step === 'auth' ? (
                <>
                    <div className="w-full flex items-start text-sm mb-5">
                        <FontAwesomeIcon icon={faInfoCircle} className="mr-2 mt-0.5 text-texts-tertiary400" title="Information" />
                        <span className="text-texts-secondary300">
                            Connect your WhatsApp Business account — you'll then pick which number to link.
                            Numbers already linked to another repo are shown but can't be selected.
                        </span>
                    </div>

                    <Button
                        text={busy ? 'Working…' : 'Connect WhatsApp Account'}
                        icon={faWhatsapp}
                        type="success"
                        onClick={handleConnectAccount}
                        disabled={!sdkReady || !repoId() || busy}
                        className="w-full py-2.5"
                    />
                    {!sdkReady && (
                        <p className="mt-2 text-center text-xs text-texts-secondary300">Loading Meta SDK…</p>
                    )}

                    {/* Manual (dev) */}
                    <div className="mt-6 pt-4 border-t border-backgrounds-border">
                        <button
                            type="button"
                            onClick={() => setShowManual((v) => !v)}
                            className="text-xs text-texts-secondary300 hover:text-texts-secondary underline"
                        >
                            {showManual ? 'Hide manual connect' : 'Use a WABA + token manually (dev)'}
                        </button>
                        {showManual && (
                            <div className="mt-3 flex flex-col gap-3">
                                <div>
                                    <label className="block text-sm font-semibold text-texts-secondary mb-1.5">
                                        WABA ID <span className="text-buttons-alert400">*</span>
                                    </label>
                                    <Input
                                        type="text"
                                        value={manual.wabaId}
                                        placeholder="WhatsApp Business Account ID"
                                        onChange={(e) => setManual((m) => ({ ...m, wabaId: e.target.value }))}
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-semibold text-texts-secondary mb-1.5">
                                        Access Token <span className="text-buttons-alert400">*</span>
                                    </label>
                                    <Input
                                        type="password"
                                        value={manual.accessToken}
                                        placeholder="System User token (recommended)"
                                        onChange={(e) => setManual((m) => ({ ...m, accessToken: e.target.value }))}
                                    />
                                </div>
                                <Button
                                    text={busy ? 'Loading…' : 'Fetch numbers'}
                                    onClick={handleFetchManual}
                                    disabled={busy}
                                    className="w-full py-2.5"
                                />
                            </div>
                        )}
                    </div>
                </>
            ) : (
                <>
                    <div className="w-full flex items-start text-sm mb-4">
                        <FontAwesomeIcon icon={faInfoCircle} className="mr-2 mt-0.5 text-texts-tertiary400" title="Information" />
                        <span className="text-texts-secondary300">
                            Pick the number to link to{' '}
                            <span className="font-semibold text-texts-secondary">
                                {contextRepo?.name || selectedRepo?.repo_name}
                            </span>. Greyed-out numbers are already linked elsewhere.
                        </span>
                    </div>

                    {numbers.length === 0 ? (
                        <div className="text-center text-texts-secondary300 py-8">
                            <p className="m-0">No phone numbers found on this account.</p>
                        </div>
                    ) : (
                        <div className="flex flex-col gap-2">
                            {numbers.map((n) => {
                                const isSel = selectedPhoneId === n.phoneNumberId && !n.linked;
                                return (
                                    <button
                                        key={n.phoneNumberId}
                                        type="button"
                                        disabled={n.linked}
                                        onClick={() => setSelectedPhoneId(n.phoneNumberId)}
                                        className={`w-full flex items-center justify-between rounded-lg p-3 text-left border transition ${
                                            n.linked
                                                ? 'border-backgrounds-border bg-backgrounds-secondary opacity-60 cursor-not-allowed'
                                                : isSel
                                                    ? 'border-buttons-primary bg-backgrounds-primary100'
                                                    : 'border-backgrounds-border hover:border-buttons-primary'
                                        }`}
                                    >
                                        <div className="flex items-center gap-3">
                                            <FontAwesomeIcon
                                                icon={faCircle}
                                                className={`text-[9px] ${isSel ? 'text-buttons-primary' : 'text-texts-secondary300'}`}
                                            />
                                            <div>
                                                <div className="text-sm font-semibold text-texts-secondary">
                                                    {n.displayNumber || n.phoneNumberId}
                                                </div>
                                                {n.verifiedName && (
                                                    <div className="text-xs text-texts-secondary300">{n.verifiedName}</div>
                                                )}
                                            </div>
                                        </div>
                                        {n.linked && (
                                            <span className="text-xs text-texts-tertiary400 flex items-center gap-1 whitespace-nowrap">
                                                <FontAwesomeIcon icon={faLock} /> Linked to {n.linkedRepoName || 'another repo'}
                                            </span>
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                    )}

                    <Button
                        text={busy ? 'Connecting…' : 'Connect selected number'}
                        icon={faWhatsapp}
                        type="success"
                        onClick={handleConnectSelected}
                        disabled={!selectedPhoneId || busy}
                        className="w-full py-2.5 mt-5"
                    />
                </>
            )}
        </div>
    );
};

export default WhatsAppConnectFlow;
